import { zodResolver } from '@hookform/resolvers/zod';
import { mfaCodeSchema } from '@willnq/shared/validation/authSchemas';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { MfaSetupResult } from '@/features/identity/types/identity.types';
import { authService } from '@/services/authService';
import { parseRestError } from '@/utils/errorUtils';

const mfaVerifySchema = z.object({ code: mfaCodeSchema });
type MfaVerifyValues = z.infer<typeof mfaVerifySchema>;

/**
 * Drives `EnableMfaDialog`'s two-stage enrollment (ADR-0006):
 *   1. `setup()` creates a PENDING enrollment and returns the QR/secret — MFA is NOT enabled yet.
 *   2. `verify(code)` confirms the first TOTP code; only then does MFA activate.
 *   3. `cancel()` discards the pending enrollment.
 * The verify code is a react-hook-form field so it reuses the shared `mfaCodeSchema` + `OtpField`,
 * exactly like the login MFA challenge. Server errors surface via `verifyError` (an <Alert>) rather
 * than `form.setError`, because the shared `OtpField` renders a red border but no message text.
 */
export function useMfaSetup() {
  const [result, setResult] = useState<MfaSetupResult | null>(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const form = useForm<MfaVerifyValues>({
    resolver: zodResolver(mfaVerifySchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { code: '' },
  });

  const setup = useCallback(async () => {
    setIsSettingUp(true);
    setSetupError(null);
    try {
      setResult(await authService.setupMfa());
    } catch (err) {
      setSetupError(parseRestError(err).message);
    } finally {
      setIsSettingUp(false);
    }
  }, []);

  /** Verifies the code; returns true on success so the dialog can close and refresh the profile. */
  const verify = useCallback(async (code: string): Promise<boolean> => {
    setVerifyError(null);
    try {
      await authService.verifyMfa(code);
      return true;
    } catch (err) {
      setVerifyError(parseRestError(err).message);
      return false;
    }
  }, []);

  /** Best-effort discard of the pending enrollment (fire-and-forget on dialog close/cancel). */
  const cancel = useCallback(async () => {
    try {
      await authService.cancelMfa();
    } catch {
      // The pending enrollment expires server-side regardless, so a failed cancel is non-fatal.
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setSetupError(null);
    setVerifyError(null);
    form.reset();
  }, [form]);

  return { form, setup, verify, cancel, reset, result, isSettingUp, setupError, verifyError };
}
